import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { Button, Grid } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';
import {
  PageTemplateDashboard,
  PageTemplateHeader,
  PageTemplateHeaderActions
} from '@sierralabs/react-material-ui';

import { FetchError } from '../../common/api/api';
import HoveredCard from '../../common/card/HoveredCard';
import { formatDate } from '../../common/formatter';
import HeaderBackButton, {
  StyledPageTemplate
} from '../../common/HeaderBackButton';
import HeaderTitle from '../../common/HeaderTitle';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import { useJiraConnectAPI } from '../jira-connect/JiraConnect.hooks';
import { useJiraServerContext } from '../jira-server/jira-server.context';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import TaskStatusVersionCard from '../tasks/cards/TaskStatusVersionCard';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import HazardControlInfoCard from './cards/HazardControlInfoCard';
import HazardReferenceCard from './cards/HazardReferenceCard';
import HazardControlVerificationCard from './cards/HazardControlVerificationCard';
import {
  useFindHazardControlById,
  useFindHazardControlVersion,
  useHazardEntityPrefixId
} from './Hazard.hooks';
import {
  BaseHazardControlsCardProps,
  HazardBoardColumnEntity,
  HazardControl
} from './Hazard.interface';
import environment from '../../environments';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';
import { useUserContext } from '../../user/User.context';

const HazardControlDetailPage: React.FC = () => {
  const {
    hazardId: paramHazardId = '',
    productId: paramProductId = '',
    controlId: paramCauseId = ''
  } = useParams<{ hazardId: string; productId: string; controlId: string }>();
  const hazardId: number = parseInt(paramHazardId) || 0;
  const productId: number = parseInt(paramProductId) || 0;
  const controlId: number = parseInt(paramCauseId) || 0;
  const { hasRole } = useUserContext();
  const [from = 'detail'] = useQueryParam('from', StringParam);
  const [version] = useQueryParam('ver', withDefault(NumberParam, undefined));
  const [hazardControl, setHazardControl] = useState<
    HazardControl | undefined
  >();
  const [, setDidUpdate] = useState<boolean>(false);
  const [, setError] = useState<FetchError | undefined>();
  const {
    hazardControl: data,
    isLoadingHazardControl,
    findHazardControlError,
    findHazardControl
  } = useFindHazardControlById(hazardId, controlId);
  const {
    hazardControlVersion: loadedHazardControlVersion,
    findHazardControlVersion
  } = useFindHazardControlVersion(hazardId, controlId, version);
  const [hazardControlVersion, setHazardControlVersion] = useState(
    loadedHazardControlVersion
  );

  const title: string = useHazardEntityPrefixId(
    hazardControl,
    HazardBoardColumnEntity.Control
  );
  const cardProps: BaseHazardControlsCardProps = {
    hazardId,
    hazardControl: hazardControl!,
    hazardControlVersion,
    onChange: (newHazardCause: Partial<HazardControl>) => {
      if (hazardControl) {
        // notify task components to refresh
        hazardControl.task = { ...hazardControl.task };
        setHazardControl({
          ...hazardControl,
          ...newHazardCause
        });
      }
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!hazardControl?.task) {
      return;
    }
    Object.assign(hazardControl.task, newTask);
    cardProps.onChange?.({});
    cardProps.onUpdate?.();
  };

  useEffect(() => {
    findHazardControl();
  }, [findHazardControl, hazardId, controlId]);
  useEffect(() => {
    if (version) {
      findHazardControlVersion();
    } else {
      setHazardControlVersion(undefined);
    }
  }, [findHazardControlVersion, hazardId, controlId, version]);
  useEffect(
    () =>
      loadedHazardControlVersion &&
      setHazardControlVersion(loadedHazardControlVersion),
    [loadedHazardControlVersion]
  );

  useEffect(() => {
    data && setHazardControl(data);
  }, [data]);

  useEffect(() => {
    setError(findHazardControlError);
  }, [findHazardControlError]);
  const { isAvailable: isJiraConnect } = useJiraConnectAPI();
  const { isJiraServer } = useJiraServerContext();
  const { isFieldDisabled } = useTaskEntityFields(hazardControl?.task);
  const task = hazardControlVersion?.task || hazardControl?.task;
  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton
          to={
            from !== 'hazardTrace'
              ? `/portal/products/${productId}/hazards/${hazardId}/${from}`
              : `/portal/products/${productId}/${from}`
          }
        />
        <HeaderTitle>
          <h2>{isLoadingHazardControl ? 'Loading...' : title}</h2>
          {hazardControl && (
            <div className='status-container'>
              <TaskStatusLabel task={hazardControl.task} />
            </div>
          )}
        </HeaderTitle>
        <PageTemplateHeaderActions flex>
          {hazardControl?.task && (
            <TaskStatusActions
              task={hazardControl.task}
              taskEntity={hazardControl}
              onChange={(task: Task) => {
                hazardControl.task = task;
                setHazardControl({ ...hazardControl });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {hazardControl && version && (
        <PageTemplateAlert severity='warning'>
          Viewing previous version {version}
          {hazardControlVersion &&
            ` from ${formatDate(hazardControlVersion.created)}. `}
          <Link to={`./${hazardControl.id}`}>
            Click here to view latest version
          </Link>
        </PageTemplateAlert>
      )}
      <PageTemplateDashboard>
        {hazardControl && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <HazardControlInfoCard {...cardProps} />
                </Grid>
                {(isJiraConnect || isJiraServer) && (
                  <Grid item xs={12}>
                    <TaskJiraIssueAssignCard
                      task={hazardControl.task}
                      entity={{
                        title: hazardControl.title,
                        description: hazardControl.description
                      }}
                      jiraFieldColumns={
                        environment.hazardConfig?.control?.visibleJiraFields
                      }
                      onChange={taskJiraIssues => {
                        // update hazardControl Task jira issues on change
                        hazardControl.task.jiraIssues = taskJiraIssues;
                        setHazardControl({ ...hazardControl });
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <HazardControlVerificationCard
                    hazardId={hazardId}
                    control={hazardControl}
                    onChange={cardProps.onChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TaskAttachmentCard
                    task={hazardControl.task}
                    disabled={Boolean(hazardControlVersion)}
                    attachments={hazardControlVersion?.attachments}
                    onError={cardProps.onError}
                    onChange={handleTaskUpdate}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TaskStatusVersionCard
                    taskEntity={hazardControl}
                    renderContent={group =>
                      group.version !== hazardControl.task.version && (
                        <HoveredCard
                          title={`Version ${group.version}`}
                          content='View the previous version'
                          actions={
                            <Button
                              variant='text'
                              color='primary'
                              component={Link}
                              startIcon={<Visibility />}
                              to={`./${hazardControl.id}?ver=${group.version}`}
                            >
                              View
                            </Button>
                          }
                        />
                      )
                    }
                  />
                </Grid>
                {task?.children && task.children.length > 0 && (
                  <Grid item xs={12}>
                    <SubTaskListCard
                      title='Stakeholders'
                      task={task}
                      disabled={
                        Boolean(version) || isFieldDisabled('assignSubTasks')
                      }
                      onError={cardProps.onError}
                      onChange={handleTaskUpdate}
                    />
                  </Grid>
                )}
                {task && (
                  <Grid item xs={12}>
                    <TaskAssignCard task={task} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TaskFileListCard
                    task={hazardControl.task}
                    disabled={Boolean(hazardControlVersion)}
                    onError={cardProps.onError}
                    onChange={handleTaskUpdate}
                  />
                </Grid>
                <Grid item xs={12}>
                  <HazardReferenceCard
                    hazardId={hazardId}
                    productId={productId}
                    sourceEntityId={controlId}
                    version={version}
                    source='controls'
                    target='causes'
                    disabled={isFieldDisabled('assignCauses')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <HazardReferenceCard
                    hazardId={hazardId}
                    productId={productId}
                    sourceEntityId={controlId}
                    version={version}
                    source='controls'
                    target='actions'
                    disabled={isFieldDisabled('assignActions')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <HazardReferenceCard
                    hazardId={hazardId}
                    productId={productId}
                    sourceEntityId={controlId}
                    version={version}
                    source='controls'
                    target='design-outputs'
                    disabled={isFieldDisabled('assignDesignOutputs')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <HazardReferenceCard
                    hazardId={hazardId}
                    productId={productId}
                    sourceEntityId={controlId}
                    version={version}
                    source='controls'
                    target='verifications'
                    disabled={isFieldDisabled('assignVerifications')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <HazardReferenceCard
                    hazardId={hazardId}
                    productId={productId}
                    sourceEntityId={controlId}
                    version={version}
                    source='controls'
                    target='validations'
                    disabled={isFieldDisabled('assignValidations')}
                  />
                </Grid>
                {hasRole('Delete') && (
                  <Grid item xs={12}>
                    <DeleteEntityCard
                      title='Hazard Control'
                      apiPath={`hazards/${hazardId}/controls`}
                      entity={hazardControl}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>
    </StyledPageTemplate>
  );
};

export default HazardControlDetailPage;
